import {
  Drawer,
  createStyles,
  TextInput,
  ScrollArea,
  Select,
  Popover,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LuX, LuSearch, LuArrowRight, LuChevronRight,
  LuCalendarDays, LuBuilding2, LuShieldCheck,
  LuFileText, LuClock, LuUsers, LuBarChart2,
  LuDollarSign, LuCheck, LuAlertTriangle,
} from "react-icons/lu";
import { getBillingProvider } from "../../services/billingprovider";
import { getpayer } from "../../services/payer";
import { getAllPatients } from "../../services/patient";
import { createBilling, getAllBillingModal } from "../../services";
import { getBillingNotes } from "../../services/note";
import { decrypt, encrypt, showError } from "../../utils";
import moment from "moment-timezone";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import swal from "sweetalert";

const useStyles = createStyles(() => ({
  drawerContent: {
    ".mantine-Drawer-content": {
      borderRadius: "16px",
      border: "1px solid #E5E5E5",
      boxShadow: "-20px 0 48px rgba(15, 23, 42, 0.18)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    ".mantine-Drawer-body": {
      padding: 0,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
    },
    ".mantine-Drawer-inner": {
      padding: "16px",
    },
  },

  modalHead: {
    padding: "22px 28px",
    borderBottom: "1px solid #F1F1F1",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexShrink: 0,
    background: "#fff",
  },

  modalEyebrow: {
    margin: "0 0 6px",
    fontSize: "11.5px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#667085",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  modalTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
    color: "#171717",
    letterSpacing: "-0.018em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  modalCloseBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    background: "#F4F4F5",
    border: 0,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    color: "#262626",
    flexShrink: 0,
    transition: "background .12s ease",
    "&:hover": {
      background: "#E4E4E7",
    },
  },

  modalBody: {
    padding: "22px 28px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  claimStepper: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 0,
    margin: "4px 0 24px",
  },

  claimStep: {
    position: "relative",
    border: 0,
    background: "transparent",
    display: "grid",
    justifyItems: "center",
    gap: "8px",
    color: "#9CA3AF",
    fontWeight: 600,
    fontSize: "12.5px",
    padding: "0 10px",
    fontFamily: "Inter, -apple-system, sans-serif",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "15px",
      left: "calc(-50% + 18px)",
      width: "calc(100% - 36px)",
      height: "2px",
      background: "#E5E5E5",
      zIndex: 0,
    },
    "&:first-of-type::before": {
      display: "none",
    },
  },

  claimStepCurrent: {
    color: "#171717",
    "&::before": {
      background: "#FFBA45",
    },
  },

  claimStepComplete: {
    color: "#171717",
    "&::before": {
      background: "#FFBA45",
    },
  },

  stepNum: {
    width: "30px",
    height: "30px",
    borderRadius: "999px",
    border: "2px solid #E5E5E5",
    background: "#fff",
    display: "grid",
    placeItems: "center",
    fontSize: "12px",
    fontWeight: 700,
    position: "relative",
    zIndex: 1,
  },

  stepNumCurrent: {
    borderColor: "#FFBA45",
    background: "#FFF4DA",
    color: "#B7791F",
    boxShadow: "0 0 0 5px rgba(255, 186, 69, 0.15)",
  },

  stepNumComplete: {
    borderColor: "#FFBA45",
    background: "#FFBA45",
    color: "#171717",
  },

  splitNote: {
    background: "transparent",
    border: 0,
    borderBottom: "1px solid #F1F1F1",
    padding: "0 0 12px",
    color: "#262626",
    fontSize: "13px",
    lineHeight: 1.55,
    marginBottom: "14px",
    fontFamily: "Inter, -apple-system, sans-serif",
    "& b": {
      color: "#171717",
      fontWeight: 700,
    },
  },

  searchWrap: {
    width: "100%",
    marginBottom: "14px",
    "& .mantine-TextInput-input": {
      height: "40px",
      paddingLeft: "38px",
      border: "1px solid #E5E5E5",
      background: "#fff",
      borderRadius: "8px",
      fontFamily: "Inter, -apple-system, sans-serif",
      fontSize: "13px",
      color: "#171717",
      "&::placeholder": {
        color: "#9CA3AF",
      },
      "&:focus": {
        borderColor: "#171717",
      },
    },
    "& .mantine-TextInput-icon": {
      color: "#9CA3AF",
      width: "38px",
    },
  },

  listPick: {
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#fff",
  },

  listPickRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    padding: "14px 16px",
    borderBottom: "1px solid #F1F1F1",
    borderLeft: "3px solid transparent",
    cursor: "pointer",
    transition: "background .12s ease",
    "&:last-of-type": {
      borderBottom: 0,
    },
    "&:hover": {
      background: "#FAFAFA",
    },
  },

  listPickRowSelected: {
    background: "#F4F4F5 !important",
    borderLeftColor: "#171717 !important",
    "&:hover": {
      background: "#F4F4F5 !important",
    },
  },

  radioInput: {
    marginTop: "4px",
    accentColor: "#171717",
    flexShrink: 0,
    cursor: "pointer",
    width: "16px",
    height: "16px",
  },

  rowInfo: {
    flex: 1,
    minWidth: 0,
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "4px",
  },

  rowStrong: {
    color: "#171717",
    fontSize: "14px",
    fontWeight: 700,
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  cmSettingsLink: {
    color: "#171717",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    cursor: "pointer",
    fontWeight: 600,
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: "Inter, -apple-system, sans-serif",
    fontSize: "inherit",
    "&:hover": { color: "#404040" },
  },

  claimAuthWarn: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 12px",
    background: "#FEF2F2",
    border: "1px solid #FCA5A5",
    borderRadius: "8px",
    color: "#B91C1C",
    fontSize: "12.5px",
    lineHeight: 1.5,
    marginBottom: "12px",
    fontFamily: "Inter, -apple-system, sans-serif",
    "& b": { color: "#B91C1C", fontWeight: 700 },
    "& svg": { width: "16px", height: "16px", flexShrink: 0, marginTop: "1px", color: "#DC2626" },
  },

  genderPill: {
    display: "inline-block",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#262626",
    background: "#FAFAFA",
    border: "1px solid #E5E5E5",
    borderRadius: "999px",
    padding: "3px 10px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcListCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },

  svcPill: {
    fontSize: "12.5px",
    color: "#262626",
    fontFamily: "Inter, -apple-system, sans-serif",
    "& b": { color: "#171717", fontWeight: 600 },
  },

  svcMoreBtn: {
    border: "1px solid #E5E5E5",
    background: "#fff",
    color: "#262626",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.05em",
    padding: "2px 8px",
    borderRadius: "999px",
    cursor: "pointer",
    lineHeight: 1.1,
    flexShrink: 0,
    "&:hover": { background: "#FAFAFA", borderColor: "#171717", color: "#171717" },
  },

  svcMorePopHead: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "6px 14px 8px",
    borderBottom: "1px solid #F1F1F1",
    marginBottom: "4px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcMorePopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "8px 14px",
    fontSize: "12.5px",
    color: "#171717",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcMorePopLeft: {
    minWidth: 0,
    "& b": { fontWeight: 700, color: "#171717", marginRight: "6px" },
  },

  svcMorePopMeta: {
    color: "#667085",
    fontSize: "11.5px",
    flexShrink: 0,
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  rowP: {
    margin: "2px 0 0",
    fontSize: "12.5px",
    color: "#667085",
    lineHeight: 1.5,
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  payerMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px",
    marginTop: "6px",
    fontSize: "12.5px",
    color: "#262626",
    alignItems: "center",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  payerMetaSpan: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },

  payerMetaB: {
    fontSize: "11px",
    color: "#667085",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginRight: "4px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  modalFoot: {
    padding: "16px 28px 22px",
    borderTop: "1px solid #F1F1F1",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    flexShrink: 0,
    background: "#fff",
  },

  btnGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    height: "40px",
    padding: "0 16px",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    fontFamily: "Inter, -apple-system, sans-serif",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    color: "#262626",
    transition: "background .12s ease, border-color .12s ease",
    "&:hover": {
      background: "#F4F4F5",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },

  btnAccent: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    height: "40px",
    padding: "0 16px",
    border: "1px solid transparent",
    borderRadius: "8px",
    fontFamily: "Inter, -apple-system, sans-serif",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    background: "#FFBA45",
    color: "#171717",
    transition: "background .12s ease",
    "&:hover": {
      background: "#EEAA33",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },

  clientTableWrap: {
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    overflow: "hidden",
    maxHeight: "360px",
    overflowY: "auto",
  },

  clientTbl: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  clientTblThead: {
    background: "#F9FAFB",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },

  clientTblTh: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "10.5px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid #E5E5E5",
    whiteSpace: "nowrap",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  clientTblTd: {
    padding: "12px",
    borderBottom: "1px solid #F1F1F1",
    whiteSpace: "nowrap",
    color: "#262626",
    fontFamily: "Inter, -apple-system, sans-serif",
    fontSize: "13px",
  },

  clientTblTdLast: {
    borderBottom: 0,
  },

  clientTblThCheckbox: {
    width: "36px",
  },

  clientTblTrHover: {
    "&:hover": {
      background: "#F9FAFB",
    },
    "&:last-of-type td": {
      borderBottom: 0,
    },
  },

  clientAvatarCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },

  clientAvatar: {
    width: "28px",
    height: "28px",
    borderRadius: "999px",
    background: "#FDE68A",
    color: "#171717",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    fontSize: "11px",
    flexShrink: 0,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },

  clientName: {
    fontWeight: 600,
    color: "#171717",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  clientCheckbox: {
    accentColor: "#171717",
    cursor: "pointer",
    width: "15px",
    height: "15px",
  },

  expiredText: {
    color: "#DC2626",
    fontWeight: 600,
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  emptyRow: {
    textAlign: "center",
    padding: "32px 12px",
    color: "#9CA3AF",
    fontFamily: "Inter, -apple-system, sans-serif",
    fontSize: "13px",
  },

  // Units step styles
  unitsSettings: {
    background: "#F9FAFB",
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    padding: "18px 20px",
    marginBottom: "18px",
  },

  unitsFormGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px 20px",
    "@media (min-width: 800px)": {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  },

  unitsField: {
    "& .mantine-Select-label, & .mantine-DatePickerInput-label": {
      fontSize: "12px",
      fontWeight: 600,
      color: "#374151",
      fontFamily: "Inter, -apple-system, sans-serif",
      marginBottom: "6px",
    },
    "& .mantine-Select-input, & .mantine-DatePickerInput-input": {
      height: "38px",
      border: "1px solid #E5E5E5",
      borderRadius: "8px",
      fontSize: "13px",
      fontFamily: "Inter, -apple-system, sans-serif",
      color: "#171717",
      "&:focus": {
        borderColor: "#171717",
      },
    },
  },

  contextBar: {
    display: "flex",
    alignItems: "stretch",
    gap: 0,
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    padding: "12px 16px",
    marginBottom: "14px",
  },

  ctxCell: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "2px 14px 2px 0",
    minWidth: 0,
  },

  ctxCellRight: {
    padding: "2px 0 2px 14px",
  },

  ctxDivider: {
    width: "1px",
    background: "#E5E5E5",
    flexShrink: 0,
  },

  ctxLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  ctxValue: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#171717",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcGroupsWrap: {
    marginBottom: "4px",
  },

  svcGroup: {
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    overflow: "hidden",
    marginBottom: "14px",
    background: "#fff",
    "&:last-of-type": {
      marginBottom: 0,
    },
  },

  svcGroupHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    background: "#F9FAFB",
    border: 0,
    borderBottom: "1px solid #F1F1F1",
    width: "100%",
    cursor: "pointer",
    fontFamily: "Inter, -apple-system, sans-serif",
    textAlign: "left",
    transition: "background .12s ease",
    "&:hover": {
      background: "#EFEFF1",
    },
  },

  svcGroupHeadClosed: {
    borderBottomColor: "transparent",
  },

  svcChev: {
    width: "18px",
    height: "18px",
    display: "grid",
    placeItems: "center",
    color: "#667085",
    transition: "transform .15s ease",
    flexShrink: 0,
  },

  svcChevOpen: {
    transform: "rotate(90deg)",
  },

  svcAvMini: {
    width: "30px",
    height: "30px",
    borderRadius: "999px",
    background: "#FDE68A",
    color: "#171717",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    fontSize: "11px",
    flexShrink: 0,
    overflow: "hidden",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },

  svcGroupNm: {
    fontWeight: 700,
    color: "#171717",
    fontSize: "13px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcCountPill: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#374151",
    background: "#fff",
    border: "1px solid #E5E5E5",
    padding: "3px 9px",
    borderRadius: "999px",
    fontFamily: "Inter, -apple-system, sans-serif",
    whiteSpace: "nowrap",
  },

  svcSubtotal: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#171717",
    fontVariantNumeric: "tabular-nums",
    minWidth: "90px",
    textAlign: "right",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcGroupBody: {
    overflowX: "auto",
  },

  svcTbl: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12.5px",
    tableLayout: "fixed",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcTblTh: {
    textAlign: "left",
    padding: "9px 10px",
    fontSize: "10px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    background: "#FAFAFA",
    borderBottom: "1px solid #F1F1F1",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  svcTblThR: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },

  svcTblTd: {
    padding: "12px 10px",
    borderBottom: "1px solid #F1F1F1",
    verticalAlign: "middle",
    fontSize: "12.5px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#262626",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcTblTdR: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },

  svcTblTrLast: {
    "& td": {
      borderBottom: 0,
    },
  },

  codeMono: {
    fontFamily:
      'ui-monospace, "SFMono-Regular", Menlo, Monaco, Consolas, monospace',
    fontWeight: 700,
    color: "#171717",
    background: "#F4F4F5",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "11.5px",
  },

  svcName: {
    fontWeight: 600,
    color: "#171717",
  },

  svcUnitsInput: {
    width: "100%",
    padding: "5px 8px",
    border: "1px solid #E5E5E5",
    borderRadius: "6px",
    fontSize: "12.5px",
    fontFamily: "Inter, -apple-system, sans-serif",
    textAlign: "right",
    background: "#fff",
    outline: "none",
    "&:focus": {
      borderColor: "#171717",
      boxShadow: "0 0 0 2px rgba(23,23,23,0.08)",
    },
  },

  svcTotalBar: {
    marginTop: "16px",
    background: "#ECFDF5",
    border: "1px solid #BBF7D0",
    borderRadius: "12px",
    padding: "16px 22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "18px",
  },

  svcTotalLbl: {
    fontSize: "11px",
    color: "#065F46",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcTotalSub: {
    fontSize: "12.5px",
    color: "#047857",
    marginTop: "4px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcTotalAmt: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#065F46",
    letterSpacing: "-0.018em",
    fontVariantNumeric: "tabular-nums",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  svcEmpty: {
    textAlign: "center",
    padding: "32px 16px",
    color: "#9CA3AF",
    fontSize: "13px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },

  // Review step
  rvSumOuter: {
    border: "1px solid #E5E5E5",
    borderRadius: "12px",
    padding: "16px 18px",
    background: "#fff",
    marginBottom: "16px",
  },
  rvSumHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingBottom: "14px",
    borderBottom: "1px solid #E5E5E5",
    marginBottom: "14px",
  },
  rvSumMeta: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  rvSumIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    background: "#fff",
    border: "1px solid #E5E5E5",
    display: "grid",
    placeItems: "center",
    color: "#171717",
    flexShrink: 0,
  },
  rvSumEyebrow: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumEmphasis: {
    fontSize: "16px",
    fontWeight: 800,
    color: "#171717",
    marginTop: "2px",
    fontVariantNumeric: "tabular-nums",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumPill: {
    background: "#FFF7E6",
    color: "#92400E",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    padding: "6px 12px",
    borderRadius: "999px",
    border: "1px solid #FED7AA",
    whiteSpace: "nowrap",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumPair: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
    marginBottom: "12px",
  },
  rvSumCard: {
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  rvSumCardHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "4px",
  },
  rvSumIconMd: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #FFE4B0, #FFBA45)",
    display: "grid",
    placeItems: "center",
    color: "#171717",
    flexShrink: 0,
  },
  rvSumCardLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumCardValue: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#171717",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumCardSub: {
    fontSize: "12.5px",
    color: "#667085",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumStats: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 0,
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    padding: "14px 16px",
    marginBottom: "18px",
  },
  rvSumStat: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
    padding: "0 14px",
    "&:first-of-type": { paddingLeft: 0 },
    "&:last-of-type": { paddingRight: 0 },
    "& + &": { borderLeft: "1px solid #F1F1F1" },
  },
  rvSumIconSm: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: "#F9FAFB",
    border: "1px solid #E5E5E5",
    display: "grid",
    placeItems: "center",
    color: "#171717",
    flexShrink: 0,
  },
  rvSumStatText: {
    minWidth: 0,
  },
  rvSumStatLbl: {
    fontSize: "10.5px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSumStatVal: {
    fontSize: "13.5px",
    fontWeight: 700,
    color: "#171717",
    marginTop: "2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcGroupsHeading: {
    margin: "0 0 10px",
    fontSize: "11px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcGroup: {
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    background: "#fff",
    marginBottom: "12px",
    overflow: "hidden",
    "&:last-of-type": { marginBottom: 0 },
  },
  rvSvcGroupHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    background: "#F9FAFB",
    borderBottom: "1px solid #F1F1F1",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcAvMini: {
    width: "26px",
    height: "26px",
    borderRadius: "999px",
    background: "#FDE68A",
    color: "#171717",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    fontSize: "11px",
    flexShrink: 0,
    overflow: "hidden",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  rvSvcGroupNm: {
    fontWeight: 700,
    color: "#171717",
    fontSize: "13px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcCountPill: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#374151",
    background: "#fff",
    border: "1px solid #E5E5E5",
    padding: "3px 9px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcSubAmt: {
    fontWeight: 800,
    color: "#171717",
    fontVariantNumeric: "tabular-nums",
    fontSize: "14px",
    minWidth: "90px",
    textAlign: "right",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcList: {
    padding: "4px 0",
  },
  rvSvcLine: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: "4px 14px",
    padding: "12px 14px",
    borderBottom: "1px solid #F1F1F1",
    alignItems: "center",
    "&:last-of-type": { borderBottom: 0 },
  },
  rvSvcLineMain: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#171717",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcLineMeta: {
    gridColumn: "1 / 2",
    fontSize: "11.5px",
    color: "#667085",
    marginTop: "2px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvSvcLineAmt: {
    gridColumn: "2 / 3",
    gridRow: "1 / span 2",
    alignSelf: "center",
    fontWeight: 700,
    color: "#171717",
    fontSize: "14px",
    fontVariantNumeric: "tabular-nums",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvTotalPanel: {
    marginTop: "18px",
    background: "linear-gradient(135deg, #1A1A1A 0%, #000000 100%)",
    color: "#fff",
    borderRadius: "14px",
    padding: "22px 26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.35)",
  },
  rvTotalLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    position: "relative",
    zIndex: 1,
    minWidth: 0,
    flex: 1,
  },
  rvTotalIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #FFDA7A, #FFBA45)",
    display: "grid",
    placeItems: "center",
    color: "#000",
    flexShrink: 0,
    boxShadow: "0 8px 20px -8px rgba(255,186,69,0.55)",
  },
  rvTotalEyebrow: {
    fontSize: "11px",
    fontWeight: 700,
    color: "rgba(255,255,255,0.55)",
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    marginBottom: "10px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvTotalChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  rvTotalChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12.5px",
    color: "rgba(255,255,255,0.75)",
    fontWeight: 500,
    fontFamily: "Inter, -apple-system, sans-serif",
    "& b": {
      color: "#fff",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
    },
  },
  rvTotalRight: {
    textAlign: "right",
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
  },
  rvTotalHelper: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    fontWeight: 700,
    color: "#FFBA45",
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    marginBottom: "6px",
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  rvTotalAmount: {
    fontSize: "38px",
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.02em",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
    fontFamily: "Inter, -apple-system, sans-serif",
  },
  btnSubmit: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    height: "40px",
    padding: "0 18px",
    border: "1px solid transparent",
    borderRadius: "8px",
    fontFamily: "Inter, -apple-system, sans-serif",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    background: "#FFBA45",
    color: "#171717",
    transition: "background .12s ease",
    "&:hover": { background: "#EEAA33" },
    "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
}));

const STEPS = ["Provider", "Payer", "Client", "Units", "Review"];

function CreateClaimDrawer({ opened, onClose, prefillClaim = null, draftData = null }) {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const { loading, allbillingprovider } = useSelector(
    (state) => state.billingprovider,
  );
  const { allpayer } = useSelector((state) => state.payer);
  const { patients } = useSelector((state) => state.patient);
  const { billingnotes } = useSelector((state) => state.note);
  const { profile } = useSelector((state) => state.profile);
  const manualOverride = !!profile?.user?.billingPreferences?.manualOverride;
  const autoBillingNotes = !!profile?.user?.billingPreferences?.autoBillingNotes;

  const rawBillingDataOptions = [
    ...(manualOverride ? [{ value: "manualbilling", label: "Manual Billing" }] : []),
    ...(autoBillingNotes ? [{ value: "billingprogressnote", label: "Billing Progress Note" }] : []),
  ];
  // Always expose at least Manual Billing so the form is usable when preferences
  // haven't been saved yet or both toggles are off.
  const billingDataOptions = rawBillingDataOptions.length > 0
    ? rawBillingDataOptions
    : [{ value: "manualbilling", label: "Manual Billing" }];

  const defaultBillingDataType = billingDataOptions[0]?.value || "manualbilling";

  const [billingLoading, setBillingLoading] = useState(false);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [currentStep, setCurrentStep] = useState(0);
  const prefillAppliedRef = useRef(false);
  const draftAppliedRef = useRef(false);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [payerclient, setPayerclient] = useState([]);
  const [billingclient, setBillingclient] = useState([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [payerSearch, setPayerSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [providerPage, setProviderPage] = useState(1);
  const [payerPage, setPayerPage] = useState(1);
  const [billingDataType, setBillingDataType] = useState(defaultBillingDataType);
  const [billingAggregate, setBillingAggregate] = useState("exactdate");
  const [billingStartDate, setBillingStartDate] = useState(new Date());
  const [billingEndDate, setBillingEndDate] = useState(new Date());
  const [serviceUnits, setServiceUnits] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [openSvcPop, setOpenSvcPop] = useState(null); // patient id

  useEffect(() => {
    if (opened) {
      if (currentStep === 0) getBillingProvider({ page: providerPage, name: providerSearch || undefined });
      if (currentStep === 1) getpayer({ page: payerPage, name: payerSearch || undefined });
      if (currentStep === 2) getAllPatients("all", true, true);
    }
  }, [opened, currentStep, providerPage, payerPage]);

  // Pre-fill from previous claim
  useEffect(() => {
    if (!opened || !prefillClaim) return;
    getBillingProvider({ page: 1 });
    getpayer({ page: 1 });
    getAllPatients("all", true, true);
  }, [opened, prefillClaim]);

  useEffect(() => {
    if (!opened || !prefillClaim) {
      prefillAppliedRef.current = false;
      return;
    }

    if (prefillAppliedRef.current) return;

    const providerList = allbillingprovider?.billingprovider || [];
    const payerList    = allpayer?.payer || [];
    const allPats      = patients?.patients || [];

    if (!providerList.length || !payerList.length || !allPats.length) return;

    const matchedProvider = providerList.find((p) => {
      const bi = p.billingIdentifier;
      const bNum = bi ? (typeof bi === "object" ? bi?.number || "" : bi) : "";
      const taxNum = p.federalTaxID?.number;
      if (prefillClaim.prov_npi && bNum === prefillClaim.prov_npi) return true;
      if (prefillClaim.bill_npi && bNum === prefillClaim.bill_npi) return true;
      if (prefillClaim.prov_id && bNum === prefillClaim.prov_id) return true;
      if (prefillClaim.bill_id && bNum === prefillClaim.bill_id) return true;
      if (prefillClaim.bill_taxid && taxNum === prefillClaim.bill_taxid) return true;
      return false;
    });
    if (matchedProvider) setSelectedProviders([matchedProvider]);

    // Payer — field in claim is `payerid` (lowercase)
    const matchedPayer = payerList.find((p) =>
      (prefillClaim.payerid && p.payerId === prefillClaim.payerid) ||
      (prefillClaim.payer_name && p.payerName?.toLowerCase() === prefillClaim.payer_name?.toLowerCase())
    );
    if (matchedPayer) setPayerclient([matchedPayer]);

    // Client — match by insurance identification number (ins_number)
    if (matchedPayer) {
      if (prefillClaim.isBatch && prefillClaim.claims?.length > 0) {
        const matchedClients = [];
        prefillClaim.claims.forEach((claim) => {
          if (!claim.ins_number) return;
          const matchedPatient = allPats.find((p) =>
            p?.form2?.BillingInsurance?.[0]?.identificationNumber === claim.ins_number
          );
          if (matchedPatient && !matchedClients.some((c) => c.selectedClient?._id === matchedPatient._id)) {
            matchedClients.push({ selectedClient: matchedPatient, payer: matchedPayer.id });
          }
        });
        if (matchedClients.length > 0) setBillingclient(matchedClients);
      } else if (prefillClaim.ins_number) {
        const matchedPatient = allPats.find((p) =>
          p?.form2?.BillingInsurance?.[0]?.identificationNumber === prefillClaim.ins_number
        );
        if (matchedPatient) {
          setBillingclient([{ selectedClient: matchedPatient, payer: matchedPayer.id }]);
        }
      }
    }

    // Dates from charge lines
    if (prefillClaim.charge?.length > 0) {
      const dates = prefillClaim.charge
        .flatMap((c) => [c.from_date, c.thru_date])
        .filter(Boolean)
        .map((d) => new Date(d))
        .filter((d) => !isNaN(d));
      if (dates.length > 0) {
        setBillingStartDate(new Date(Math.min(...dates)));
        setBillingEndDate(new Date(Math.max(...dates)));
      }
    }

    // Steps 1-3 auto-filled — open at step 4 (Units)
    setCurrentStep(3);
    prefillAppliedRef.current = true;
  }, [opened, prefillClaim, allbillingprovider, allpayer, patients]);

  // Load data for draft restore
  useEffect(() => {
    if (!opened || !draftData) return;
    getBillingProvider({ page: 1 });
    getpayer({ page: 1 });
    getAllPatients("all", true, true);
  }, [opened, draftData]);

  // Apply draft data once lists are loaded
  useEffect(() => {
    if (!opened || !draftData) {
      draftAppliedRef.current = false;
      return;
    }
    if (draftAppliedRef.current) return;

    const providerList = allbillingprovider?.billingprovider || [];
    const payerList    = allpayer?.payer || [];
    const allPats      = patients?.patients || [];

    if (!providerList.length || !payerList.length || !allPats.length) return;

    // Provider: match from Redux list by id (already has correct structure)
    const draftProviderId = draftData.provider?.id || draftData.provider;
    const matchedProvider = providerList.find((p) => p.id === draftProviderId) || providerList[0];
    if (matchedProvider) setSelectedProviders([matchedProvider]);

    // Payer: match from Redux list so it has full structure including payerId
    const draftPayerId = draftData.payer?.id || draftData.payer;
    const matchedPayer = payerList.find((p) => p.id === draftPayerId);
    if (matchedPayer) setPayerclient([matchedPayer]);

    // Clients: match against decrypted Redux patients so form1/name are readable
    if (draftData.client?.length > 0 && matchedPayer) {
      const draftClientIds = draftData.client.map((c) => c.id || c._id || c);
      const clientItems = draftClientIds
        .map((id) => allPats.find((p) => p.id === id || p._id === id))
        .filter(Boolean)
        .map((patient) => ({ selectedClient: patient, payer: matchedPayer.id }));
      if (clientItems.length > 0) setBillingclient(clientItems);
    }

    // Restore billing service dates
    const dates = draftData.billingServicesDates || {};
    if (dates.billingData)   setBillingDataType(dates.billingData);
    if (dates.billSchedule)  setBillingAggregate(dates.billSchedule);
    if (dates.startDate)     setBillingStartDate(new Date(dates.startDate));
    if (dates.endDate)       setBillingEndDate(new Date(dates.endDate));

    // Jump to Units step
    setCurrentStep(3);
    draftAppliedRef.current = true;
  }, [opened, draftData, allbillingprovider, allpayer, patients]);

  // Fetch billing notes whenever the date range or billing type changes so
  // billingprogressnote mode has up-to-date notes data.
  useEffect(() => {
    if (!opened || billingDataType !== "billingprogressnote") return;
    if (!billingStartDate || !billingEndDate) return;
    const start_date = moment.tz(billingStartDate, userTimezone).format("YYYY-MM-DD");
    const end_date = moment.tz(billingEndDate, userTimezone).format("YYYY-MM-DD");
    getBillingNotes({ start_date, end_date });
  }, [opened, billingDataType, billingStartDate, billingEndDate]);

  function getDateRanges(start, end, billingschedule) {
    const ranges = [];
    let current = moment.tz(start, userTimezone).clone();
    const final = moment.tz(end, userTimezone).clone();

    if (billingschedule === "monthly") {
      while (current.isSameOrBefore(final, "month")) {
        const s = current.clone().startOf("month");
        const e = current.clone().endOf("month");
        ranges.push({ label: `${s.format("YYYY-MM-DD")} - ${e.format("YYYY-MM-DD")}`, start: s, end: e });
        current.add(1, "month");
      }
    } else if (billingschedule === "weekly") {
      current = current.startOf("day");
      let isFirst = true;
      while (current.isSameOrBefore(final)) {
        let ws = isFirst ? current.clone() : current.clone().startOf("week");
        let we = isFirst ? current.clone().add(6 - current.day(), "days") : current.clone().endOf("week");
        if (we.isAfter(final)) we = final.clone();
        ranges.push({ label: `${ws.format("YYYY-MM-DD")} - ${we.format("YYYY-MM-DD")}`, start: ws.clone().startOf("day"), end: we.clone().endOf("day") });
        current = we.clone().add(1, "day").startOf("day");
        isFirst = false;
      }
    } else if (billingschedule === "exactdate") {
      const s = moment.tz(start, userTimezone).startOf("day");
      const e = moment.tz(end, userTimezone).endOf("day");
      ranges.push({ label: `${s.format("YYYY-MM-DD")} - ${e.format("YYYY-MM-DD")}`, start: s, end: e });
    } else {
      while (current.isSameOrBefore(final, "day")) {
        const s = current.clone().startOf("day");
        const e = current.clone().endOf("day");
        ranges.push({ label: `${s.format("YYYY-MM-DD")} - ${e.format("YYYY-MM-DD")}`, start: s, end: e });
        current.add(1, "day");
      }
    }
    return ranges;
  }

  const finalservices = useMemo(() => {
    if (!billingStartDate || !billingEndDate || billingclient.length === 0) return [];

    const start = moment.tz(billingStartDate, userTimezone).startOf("day");
    const end = moment.tz(billingEndDate, userTimezone).endOf("day");
    const dateRanges = getDateRanges(start, end, billingAggregate);
    const result = [];

    billingclient.forEach((clientEntry) => {
      const services = clientEntry.selectedClient?.form1?.services || [];
      services.forEach((service) => {
        if (billingDataType === "billingprogressnote") {
          dateRanges.forEach((range) => {
            const notesInRange = billingnotes?.data?.filter((note) => {
              const serviceType = note?.fields?.["Service Type"];
              const clientId = note?.fields?.["Client Name"];
              const noteDate = moment.tz(note?.fields?.["Service Date"], userTimezone);
              return (
                serviceType === service?.servicenumber &&
                clientId === clientEntry.selectedClient?.id &&
                noteDate.isBetween(range.start, range.end, null, "[]")
              );
            }) || [];

            if (notesInRange.length === 0) return;

            const totalUnits = notesInRange
              .map((note) =>
                note?.fields?.["Unit/Day/Hours"]?.split(",").reduce((sum, val) => sum + parseFloat(val), 0) || 0
              )
              .reduce((a, b) => a + b, 0);

            result.push({
              ...service,
              patientId: clientEntry.selectedClient?.id,
              serviceRate: totalUnits,
              totalCost: totalUnits * (parseFloat(service?.servicechargeNumber) || 0),
              serviceDate: range.label,
              billingschedule: billingAggregate,
            });
          });
        } else {
          // manualbilling — one row per service with full date range
          const label = `${start.format("YYYY-MM-DD")} - ${end.format("YYYY-MM-DD")}`;
          const unitVal = parseFloat(service?.servicechargeAmount) || 0;
          result.push({
            ...service,
            patientId: clientEntry.selectedClient?.id,
            serviceRate: unitVal,
            totalCost: unitVal * (parseFloat(service?.servicechargeNumber) || 0),
            serviceDate: label,
            billingschedule: billingAggregate,
          });
        }
      });
    });

    return result;
  }, [billingStartDate, billingEndDate, billingAggregate, billingDataType, billingclient, billingnotes]);

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedProviders([]);
    setPayerclient([]);
    setBillingclient([]);
    setProviderSearch("");
    setPayerSearch("");
    setClientSearch("");
    setProviderPage(1);
    setPayerPage(1);
    setBillingDataType(defaultBillingDataType);
    setBillingAggregate("exactdate");
    setBillingStartDate(new Date());
    setBillingEndDate(new Date());
    setServiceUnits({});
    setOpenGroups({});
    setOpenSvcPop(null);
    setBillingLoading(false);
    onClose();
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleProviderClick = (item) => {
    const isSelected = selectedProviders.some((p) => p.id === item.id);
    if (isSelected) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders([item]);
    }
  };

  const handlePayerClick = (payer) => {
    const isSelected = payerclient.some((p) => p.id === payer.id);
    if (!isSelected) {
      setPayerclient([payer]);
    } else {
      setPayerclient([]);
    }
  };

  const handleClientSelection = (value, payerid) => {
    if (!payerid) return;
    const selectedClient = patients?.patients?.find((p) => p.id === value.id);
    if (!selectedClient) return;
    if (
      !billingclient.some(
        (c) => c.payer === payerid && c.selectedClient?.id === selectedClient.id,
      )
    ) {
      setBillingclient((prev) => [...prev, { selectedClient, payer: payerid }]);
    }
  };

  const handleClientRemove = (patient) => {
    setBillingclient((prev) =>
      prev.filter((c) => c.selectedClient?.id !== patient.id),
    );
  };

  const handleNext = () => {
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const hasValidUnits =
    finalservices.length > 0 &&
    billingclient.every((billing_client) => {
      const patientServices = finalservices.filter(
        (s) => s.patientId === billing_client.selectedClient?.id
      );
      return patientServices.some((service, si) => {
        const key = `${billing_client.selectedClient.id}_${si}`;
        return parseFloat(serviceUnits[key]) > 0;
      });
    });

  const canContinue =
    (currentStep === 0 && selectedProviders.length > 0) ||
    (currentStep === 1 && payerclient.length > 0) ||
    (currentStep === 2 && billingclient.length > 0) ||
    (currentStep === 3 && billingStartDate !== null && billingEndDate !== null && hasValidUnits) ||
    currentStep === 4;

  function roundToTwoDecimals(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  function formatDateYMD(dateString) {
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const validateClaim = () => {
    const provider = selectedProviders[0];
    const npi = getProviderNPI(provider);
    const bid = getProviderID(provider);
    if (!npi && !bid) {
      showError("Provider is missing a Billing Identifier (NPI or ID). Please update the provider before submitting a claim.", 400);
      return false;
    }
    if (billingclient.length === 0) {
      showError("Please select at least one client.", 400);
      return false;
    }
    if (!billingStartDate || !billingEndDate) {
      showError("Please set service start and end dates.", 400);
      return false;
    }
    if (finalservices.length === 0) {
      showError(
        billingDataType === "billingprogressnote"
          ? "No billing progress notes found for the selected clients and date range. Check the date range or switch to Manual Billing."
          : "No services found for the selected clients. Please verify the client has services configured.",
        400
      );
      return false;
    }
    return true;
  };

  const getProviderNPI = (provider) => {
    const bi = provider?.billingIdentifier;
    if (!bi) return "";
    if (typeof bi === "object") return bi?.type === "NPI" ? bi?.number || "" : "";
    return bi;
  };

  const getProviderID = (provider) => {
    const bi = provider?.billingIdentifier;
    if (!bi || typeof bi !== "object") return "";
    return bi?.type !== "NPI" ? bi?.number || "" : "";
  };

  const handleSubmitClaim = async () => {
    if (!validateClaim()) return;
    try {
      setBillingLoading(true);

      const billingdata = {
        filename: "",
        file: {},
        facilityId: null,
        patientId: null,
        admin: null,
        createdBy: null,
        billingForm: null,
      };

      const jsondata = { fileid: "124", claim: [] };
      const batchId = "BATCH-" + Math.floor(100000 + Math.random() * 900000);

      billingclient?.forEach((billing_client) => {
        if (!billing_client?.payer) return;
        const matchingPayer = payerclient.find((p) => p.id === billing_client.payer);
        if (!matchingPayer) return;

        const nameParts = selectedProviders[0]?.name?.split(" ") || [];
        const diagnosisArray = billing_client?.selectedClient?.form1?.diagnosis || [];
        let diagnosisFields = {};
        diagnosisArray.forEach((item, i) => {
          diagnosisFields[`diag_${i + 1}`] = item?.code || null;
        });

        // Build charges using user-edited serviceUnits (step 3) when available,
        // falling back to the computed serviceRate from finalservices.
        const patientFinalServices = finalservices.filter(
          (s) => s.patientId === billing_client.selectedClient.id
        );

        const charges = patientFinalServices
          .map((service, si) => {
            const key = `${billing_client.selectedClient.id}_${si}`;
            const units = parseFloat(serviceUnits[key]) || 0;
            const rate = parseFloat(service?.servicechargeNumber) || 0;
            return {
              charge: (units * rate).toFixed(2),
              charge_record_type: "UN",
              diag_ref: "A",
              from_date: service?.serviceDate?.split(" - ")[0] || "",
              thru_date: service?.serviceDate?.split(" - ")[1] || "",
              place_of_service: selectedProviders[0]?.serviceCode?.code || "",
              proc_code: service?.servicenumber || "",
              mod1: service?.mod1 || "",
              remote_chgid: "",
              units: String(units) || "1",
              _units: units,
            };
          })
          .filter((ch) => ch._units !== 0)
          .map(({ _units, ...ch }) => ch);

        const claim_ = {
          accept_assign: "Y",
          auto_accident: "N",
          balance_due: "",
          bill_addr_1: selectedProviders[0]?.address || "",
          bill_city: selectedProviders[0]?.city || "",
          bill_name: selectedProviders[0]?.name || "",
          bill_npi: getProviderNPI(selectedProviders[0]),
          bill_id: getProviderID(selectedProviders[0]),
          bill_phone: selectedProviders[0]?.phone || "",
          bill_state: selectedProviders[0]?.state || "",
          bill_taxid: selectedProviders[0]?.federalTaxID?.number || "",
          bill_taxid_type: selectedProviders[0]?.federalTaxID?.type === "EIN" ? "E" : "S",
          bill_zip: selectedProviders[0]?.zipcode || "",
          diagnosis: diagnosisArray,
          charge: charges,
          claim_form: "837p",
          ...diagnosisFields,
          employment_related: "N",
          ins_addr_1: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.address1 || "",
          ins_city: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.city || "",
          ins_dob: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.dob || "",
          ins_group: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.groupPolicyNumber || "",
          ins_name_f: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.firstName || "",
          ins_name_l: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.lastName || "",
          ins_number: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.identificationNumber || "",
          ins_sex: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.gender === "Male" ? "M" : "F",
          ins_state: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.state || "",
          ins_zip: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.zipCode || "",
          pat_addr_1: billing_client?.selectedClient?.form1?.previousAddress || "",
          pat_city: billing_client?.selectedClient?.form1?.city || "",
          pat_dob: formatDateYMD(billing_client?.selectedClient?.form1?.dob),
          pat_name_f: billing_client?.selectedClient?.form1?.firstName || "",
          pat_name_l: billing_client?.selectedClient?.form1?.lastName || "",
          pat_rel: "18",
          pat_sex: billing_client?.selectedClient?.form1?.gender === "Male" ? "M" : "F",
          pat_state: billing_client?.selectedClient?.form1?.state || "",
          pat_zip: billing_client?.selectedClient?.form1?.zipCode || "",
          payer_addr_1: matchingPayer.payerAddress1 || "",
          payer_city: matchingPayer.payerCity || "",
          payer_name: matchingPayer.payerName || "",
          payer_order: matchingPayer.payerOrder || "",
          payer_state: matchingPayer.payerState || "",
          payer_zip: matchingPayer.payerZip || "",
          payerid: matchingPayer.payerId || "",
          pcn: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.identificationNumber || "",
          prov_name_f: nameParts[0] || "",
          prov_name_l: nameParts[1] || "",
          prov_name_m: selectedProviders[0]?.facilityId?.name || "",
          prov_npi: "",
          prov_id: "",
          prov_taxonomy: selectedProviders[0]?.taxonomy || "",
          ref_name_f: "", ref_name_l: "", ref_name_m: "", ref_npi: "",
          remote_batchid: batchId, remote_claimid: "", remote_fileid: "",
          prior_auth: billing_client?.selectedClient?.form2?.BillingInsurance?.[0]?.authorization || "",
          total_charge: patientFinalServices
            .reduce((total, service, si) => {
              const key = `${billing_client.selectedClient.id}_${si}`;
              const units = parseFloat(serviceUnits[key]) || 0;
              const rate = parseFloat(service?.servicechargeNumber) || 0;
              return roundToTwoDecimals(total + units * rate);
            }, 0)
            .toFixed(2),
        };

        jsondata.claim.push(claim_);
      });

      billingdata.filename = `${selectedProviders[0]?.name || "new claim"}_${Date.now()}`;
      billingdata.file = encrypt(jsondata);
      billingdata.createdBy = profile?.user?.id;
      billingdata.admin = profile?.user?.admin ?? profile?.user?.id;
      billingdata.billingForm = encrypt(jsondata);

      const res = await createBilling(billingdata);
      if (res?.status === 200) {
        swal({ title: "Claim submitted successfully", icon: "success", dangerMode: false });
        handleClose();
        getAllBillingModal({ page: 1 });
      } else if (res?.awaitedError) {
        handleClose();
        getAllBillingModal({ page: 1 });
      }
      setBillingLoading(false);
    } catch (error) {
      console.error("Submit claim error:", error);
      setBillingLoading(false);
    }
  };

  const SearchBillingProvider = async (value) => {
    setProviderSearch(value);
    setProviderPage(1);
    try {
      await getBillingProvider({ name: value, page: 1 });
    } catch (e) {
      console.error(e);
    }
  };

  const SearchBillingPayer = async (value) => {
    setPayerSearch(value);
    setPayerPage(1);
    try {
      await getpayer({ name: value, page: 1 });
    } catch (e) {
      console.error(e);
    }
  };

  const getStepClass = (idx) => {
    if (idx === currentStep)
      return cx(classes.claimStep, classes.claimStepCurrent);
    if (idx < currentStep)
      return cx(classes.claimStep, classes.claimStepComplete);
    return classes.claimStep;
  };

  const getStepNumClass = (idx) => {
    if (idx === currentStep) return cx(classes.stepNum, classes.stepNumCurrent);
    if (idx < currentStep) return cx(classes.stepNum, classes.stepNumComplete);
    return classes.stepNum;
  };

  const providers = allbillingprovider?.billingprovider || [];
  const payers = allpayer?.payer || [];

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="70vw"
      withCloseButton={false}
      padding={0}
      zIndex={1000}
      className={classes.drawerContent}
    >
      {/* era-modal-head */}
      <div className={classes.modalHead}>
        <div>
          <p className={classes.modalEyebrow}>Start Claim</p>
          <h2 className={classes.modalTitle}>Professional Claim (837p)</h2>
        </div>
        <button
          className={classes.modalCloseBtn}
          onClick={handleClose}
          aria-label="Close"
        >
          <LuX size={18} />
        </button>
      </div>

      {/* era-modal-body */}
      <ScrollArea style={{ flex: 1, overflow: "hidden" }}>
        <div className={classes.modalBody}>
          {/* claim-stepper */}
          <div className={classes.claimStepper}>
            {STEPS.map((label, idx) => (
              <div key={idx} className={getStepClass(idx)}>
                <span className={getStepNumClass(idx)}>{idx + 1}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Step 0: Provider */}
          {currentStep === 0 && (
            <div>
              <div className={classes.splitNote}>
                <span>
                  <b>Select a Provider from your list of Providers.</b> You can always add new Providers from{" "}
                  <button
                    type="button"
                    className={classes.cmSettingsLink}
                    onClick={() => navigate("/home/billing-settings", { state: { tab: "provider" } })}
                  >Settings &gt; Billing Provider</button>.
                </span>
              </div>

              <div className={classes.searchWrap}>
                <TextInput
                  value={providerSearch}
                  onChange={(e) => SearchBillingProvider(e.currentTarget.value)}
                  placeholder="Search provider"
                  icon={<LuSearch size={16} />}
                />
              </div>

              <div className={classes.listPick}>
                {loading && (
                  <div className={classes.listPickRow}>
                    <div className={classes.rowInfo}>
                      <p className={classes.rowP}>Loading providers...</p>
                    </div>
                  </div>
                )}
                {!loading && providers.length === 0 && (
                  <div className={classes.listPickRow}>
                    <div className={classes.rowInfo}>
                      <p className={classes.rowP}>No providers found.</p>
                    </div>
                  </div>
                )}
                {!loading &&
                  providers.map((item) => {
                    const isSelected = selectedProviders.some(
                      (p) => p.id === item.id,
                    );
                    return (
                      <div
                        key={item?.id}
                        className={cx(classes.listPickRow, {
                          [classes.listPickRowSelected]: isSelected,
                        })}
                        onClick={() => handleProviderClick(item)}
                      >
                        <input
                          type="radio"
                          name="claim-provider"
                          className={classes.radioInput}
                          checked={isSelected}
                          onChange={() => handleProviderClick(item)}
                        />
                        <div className={classes.rowInfo}>
                          <div className={classes.titleRow}>
                            <strong className={classes.rowStrong}>
                              {item?.name || "-"}
                            </strong>
                          </div>
                          <p className={classes.rowP}>
                            {[
                              item?.providertype || null,
                              (() => { const bi = item?.billingIdentifier; const n = bi ? (typeof bi === "object" ? bi?.number || "" : bi) : ""; return n ? `NPI ${n}` : null; })(),
                              item?.federalTaxID?.number
                                ? `Tax ID ${item.federalTaxID.number}${item.federalTaxID.type ? ` (${item.federalTaxID.type})` : ""}`
                                : null,
                              item?.taxonomy
                                ? `Taxonomy ${item.taxonomy}`
                                : null,
                              item?.serviceCode?.name
                                ? item.serviceCode.name
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          <p className={classes.rowP}>
                            {[
                              item?.address,
                              item?.city && item?.state && item?.zipcode
                                ? `${item.city} ${item.state} ${item.zipcode}`
                                : item?.city ||
                                  item?.state ||
                                  item?.zipcode ||
                                  null,
                              item?.phone ? item.phone : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {allbillingprovider?.count > 0 && (
                <CustomBillingPagination
                  data={allbillingprovider.count}
                  itemsPerPage={10}
                  currentPage={providerPage}
                  handlePageChange={(p) => setProviderPage(p)}
                />
              )}
            </div>
          )}

          {/* Step 1: Payer */}
          {currentStep === 1 && (
            <div>
              <div className={classes.splitNote}>
                <span>
                  <b>Select a Payer to bill.</b> You can always add new Payers from{" "}
                  <button
                    type="button"
                    className={classes.cmSettingsLink}
                    onClick={() => navigate("/home/billing-settings", { state: { tab: "payer" } })}
                  >Settings &gt; Payer</button>.
                </span>
              </div>

              <div className={classes.searchWrap}>
                <TextInput
                  value={payerSearch}
                  onChange={(e) => SearchBillingPayer(e.currentTarget.value)}
                  placeholder="Search Payers"
                  icon={<LuSearch size={16} />}
                />
              </div>

              <div className={classes.listPick}>
                {payers.length === 0 && (
                  <div className={classes.listPickRow}>
                    <div className={classes.rowInfo}>
                      <p className={classes.rowP}>No payers found.</p>
                    </div>
                  </div>
                )}
                {payers.map((payer, index) => {
                  const isSelected = payerclient.some((p) => p.id === payer.id);
                  return (
                    <div
                      key={index}
                      className={cx(classes.listPickRow, {
                        [classes.listPickRowSelected]: isSelected,
                      })}
                      onClick={() => handlePayerClick(payer)}
                    >
                      <input
                        type="radio"
                        name="claim-payer"
                        className={classes.radioInput}
                        checked={isSelected}
                        onChange={() => handlePayerClick(payer)}
                      />
                      <div className={classes.rowInfo}>
                        <div className={classes.titleRow}>
                          <strong className={classes.rowStrong}>
                            {payer.payerName || "-"}
                          </strong>
                        </div>
                        <div className={classes.payerMetaRow}>
                          {payer.payerId && (
                            <span className={classes.payerMetaSpan}>
                              <b className={classes.payerMetaB}>Payer ID</b>
                              {payer.payerId}
                            </span>
                          )}
                          {payer.payerEntity && (
                            <span className={classes.payerMetaSpan}>
                              <b className={classes.payerMetaB}>Type</b>
                              {payer.payerEntity}
                            </span>
                          )}
                          {payer.payerState && (
                            <span className={classes.payerMetaSpan}>
                              <b className={classes.payerMetaB}>State</b>
                              {payer.payerState}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {allpayer?.count > 0 && (
                <CustomBillingPagination
                  data={allpayer.count}
                  itemsPerPage={10}
                  currentPage={payerPage}
                  handlePageChange={(p) => setPayerPage(p)}
                />
              )}
            </div>
          )}

          {/* Step 2: Client */}
          {currentStep === 2 && (() => {
            const allPatients = patients?.patients || [];
            const filteredPatients = allPatients.filter((patient) => {
              if (clientSearch) {
                const name = (patient?.name || "").toLowerCase();
                if (!name.includes(clientSearch.toLowerCase())) return false;
              }
              const payerInfo = patient.form2?.BillingInsurance?.[0]?.payer ?? null;
              const providerInfo = patient?.providerId?.[0]?.id ?? null;
              const isAuthorizationExpired = patient?.form2?.BillingInsurance?.[0]?.authorizationEndDate
                ? moment.tz(patient.form2.BillingInsurance[0].authorizationEndDate, userTimezone).isBefore(moment.tz(new Date(), userTimezone), "day")
                : false;
              const isPayerMatch =
                payerInfo &&
                providerInfo &&
                !isAuthorizationExpired &&
                payerclient?.some(
                  (payer) =>
                    payer.id === payerInfo.value &&
                    providerInfo === selectedProviders[0]?.id,
                );
              return isPayerMatch;
            });

            const allVisibleSelected =
              filteredPatients.length > 0 &&
              filteredPatients.every((patient) =>
                billingclient.some((c) => c.selectedClient?.id === patient.id),
              );

            const handleSelectAll = (checked) => {
              if (checked) {
                const toAdd = filteredPatients.filter(
                  (patient) =>
                    !billingclient.some((c) => c.selectedClient?.id === patient.id),
                );
                toAdd.forEach((patient) => {
                  const payerInfo = patient.form2?.BillingInsurance?.[0]?.payer ?? null;
                  handleClientSelection(patient, payerInfo?.value ?? null);
                });
              } else {
                filteredPatients.forEach((patient) => handleClientRemove(patient));
              }
            };

            return (
              <div>
                <div className={classes.splitNote}>
                  <span>
                    <b>Select Client(s) to bill.</b> You can always add new Clients from{" "}
                    <button
                      type="button"
                      className={classes.cmSettingsLink}
                      onClick={() => navigate("/home/billing-settings", { state: { tab: "client" } })}
                    >Settings &gt; Clients</button>.
                  </span>
                </div>
                {filteredPatients.some((p) =>
                  p?.form2?.BillingInsurance?.[0]?.authorizationEndDate &&
                  moment.tz(p.form2.BillingInsurance[0].authorizationEndDate, userTimezone).isBefore(moment.tz(new Date(), userTimezone), "day")
                ) && (
                  <div className={classes.claimAuthWarn}>
                    <LuAlertTriangle />
                    <div>
                      <b>One or more clients have expired authorizations.</b>
                      {" "}Renew the authorization code and end date before submitting this claim.
                    </div>
                  </div>
                )}
                <div className={classes.searchWrap}>
                  <TextInput
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.currentTarget.value)}
                    placeholder="Search clients"
                    icon={<LuSearch size={16} />}
                  />
                </div>
                <div className={classes.clientTableWrap}>
                  <table className={classes.clientTbl}>
                    <thead className={classes.clientTblThead}>
                      <tr>
                        <th className={cx(classes.clientTblTh, classes.clientTblThCheckbox)}>
                          <input
                            type="checkbox"
                            className={classes.clientCheckbox}
                            checked={allVisibleSelected}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>
                        <th className={classes.clientTblTh}>Client Name</th>
                        <th className={classes.clientTblTh}>City · State</th>
                        <th className={classes.clientTblTh}>Gender</th>
                        <th className={classes.clientTblTh}>Service</th>
                        <th className={classes.clientTblTh}>Authorization #</th>
                        <th className={classes.clientTblTh}>Auth End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPatients.length === 0 && (
                        <tr>
                          <td colSpan={7} className={classes.emptyRow}>
                            No clients found matching the selected provider and payer.
                          </td>
                        </tr>
                      )}
                      {filteredPatients.map((patient, index) => {
                        const payerInfo = patient.form2?.BillingInsurance?.[0]?.payer ?? null;
                        const isAuthorizationExpired = patient?.form2?.BillingInsurance?.[0]?.authorizationEndDate
                          ? moment.tz(patient.form2.BillingInsurance[0].authorizationEndDate, userTimezone).isBefore(moment.tz(new Date(), userTimezone), "day")
                          : false;
                        const isSelected = billingclient.some(
                          (c) => c.selectedClient?.id === patient.id,
                        );
                        const authId = patient?.form2?.BillingInsurance?.[0]?.authorization || "-";
                        const authEndDate = patient?.form2?.BillingInsurance?.[0]?.authorizationEndDate;
                        const authExpires = authEndDate
                          ? new Date(authEndDate).toLocaleDateString("en-US")
                          : "-";
                        const initials = (patient?.name || "?")
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((w) => w[0].toUpperCase())
                          .join("");
                        const city = patient?.city || patient?.form1?.city || "-";
                        const state = patient?.state || patient?.form1?.state || "-";
                        const gender = patient?.form1?.gender || "-";
                        const firstService = patient?.form1?.services?.[0];
                        const profileImg = patient?.imageurl ? decrypt(patient.imageurl) : null;

                        return (
                          <tr
                            key={index}
                            className={classes.clientTblTrHover}
                            onClick={() => {
                              if (isSelected) {
                                handleClientRemove(patient);
                              } else {
                                handleClientSelection(patient, payerInfo?.value ?? null);
                              }
                            }}
                          >
                            <td className={classes.clientTblTd}>
                              <input
                                type="checkbox"
                                className={classes.clientCheckbox}
                                checked={isSelected}
                                onChange={() => {}}
                              />
                            </td>
                            <td className={classes.clientTblTd}>
                              <div className={classes.clientAvatarCell}>
                                <span
                                  className={classes.clientAvatar}
                                  style={profileImg ? { backgroundImage: `url('${profileImg}')` } : {}}
                                >
                                  {!profileImg && initials}
                                </span>
                                <span className={classes.clientName}>{patient?.name || "-"}</span>
                              </div>
                            </td>
                            <td className={classes.clientTblTd}>{city} · {state}</td>
                            <td className={classes.clientTblTd}>
                              <span className={classes.genderPill}>{gender}</span>
                            </td>
                            <td className={classes.clientTblTd}>
                              <div className={classes.svcListCell}>
                                <span className={classes.svcPill}>
                                  {firstService?.servicenumber && <b>{firstService.servicenumber}</b>}
                                  {firstService?.servicenumber && firstService?.servicetitle && " · "}
                                  {firstService?.servicetitle || "-"}
                                </span>
                                {patient?.form1?.services?.length > 1 && (
                                  <Popover
                                    opened={openSvcPop === patient.id}
                                    onClose={() => setOpenSvcPop(null)}
                                    position="bottom-start"
                                    withArrow={false}
                                    shadow="md"
                                    width={340}
                                    zIndex={1600}
                                    withinPortal
                                    styles={{
                                      dropdown: {
                                        padding: "8px 0",
                                        border: "1px solid #E5E5E5",
                                        borderRadius: "8px",
                                        boxShadow: "0 12px 28px rgba(0,0,0,0.16)",
                                      },
                                    }}
                                  >
                                    <Popover.Target>
                                      <button
                                        type="button"
                                        className={classes.svcMoreBtn}
                                        title="View all services"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenSvcPop(openSvcPop === patient.id ? null : patient.id);
                                        }}
                                      >···</button>
                                    </Popover.Target>
                                    <Popover.Dropdown onClick={(e) => e.stopPropagation()}>
                                      <div className={classes.svcMorePopHead}>{patient?.name} — All Services</div>
                                      {patient.form1.services.map((svc, i) => (
                                        <div key={i} className={classes.svcMorePopRow}>
                                          <div className={classes.svcMorePopLeft}>
                                            {svc.servicenumber && <b>{svc.servicenumber}</b>}
                                            {svc.servicetitle || "-"}
                                          </div>
                                          <div className={classes.svcMorePopMeta}>
                                            {svc.servicechargetype || ""}
                                            {svc.servicechargeAmount ? ` · ${svc.servicechargeAmount} units` : ""}
                                          </div>
                                        </div>
                                      ))}
                                    </Popover.Dropdown>
                                  </Popover>
                                )}
                              </div>
                            </td>
                            <td className={classes.clientTblTd}>{authId}</td>
                            <td className={classes.clientTblTd}>
                              {isAuthorizationExpired ? (
                                <span className={classes.expiredText}>{authExpires} (expired)</span>
                              ) : (
                                authExpires
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Step 3: Units */}
          {currentStep === 3 && (() => {
            const providerName = selectedProviders[0]?.name || "-";
            const providerType = selectedProviders[0]?.providertype || null;
            const providerLabel = [providerName, providerType].filter(Boolean).join(" · ");

            const payer = payerclient[0];
            const payerLabel = [
              payer?.payerName,
              payer?.payerState,
              payer?.payerId ? `Payer ID ${payer.payerId}` : null,
            ].filter(Boolean).join(" · ") || "-";

            // Build per-client service rows driven by finalservices (same as BillingClaimForm)
            let totalServiceCount = 0;
            let totalUnitCount = 0;
            let totalAmount = 0;

            const clientRows = billingclient.map((clientEntry) => {
              const patient = clientEntry.selectedClient;
              const initials = (patient?.name || "?")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0].toUpperCase())
                .join("");
              const profileImg = patient?.imageurl ? decrypt(patient.imageurl) : null;
              const isOpen = openGroups[patient.id] !== false;

              // filter finalservices for this patient
              const patientServices = finalservices.filter(
                (s) => s.patientId === patient.id
              );

              let clientSubtotal = 0;
              const serviceRows = patientServices.map((svc, si) => {
                const key = `${patient.id}_${si}`;
                const startDateStr = svc.serviceDate?.split(" - ")[0] || "";
                const endDateStr = svc.serviceDate?.split(" - ")[1] || "";
                const unitVal = serviceUnits[key] !== undefined ? serviceUnits[key] : "";
                const rate = parseFloat(svc.servicechargeNumber) || 0;
                const units = parseFloat(unitVal) || 0;
                const charge = units * rate;
                clientSubtotal += charge;
                totalUnitCount += units;
                totalAmount += charge;
                totalServiceCount += 1;
                return { svc, key, startDateStr, endDateStr, unitVal, rate, charge };
              });

              return { patient, initials, profileImg, isOpen, serviceRows, clientSubtotal };
            });

            return (
              <div>
                {/* Settings bar */}
                <div className={classes.unitsSettings}>
                  <div className={classes.unitsFormGrid}>
                    <div className={classes.unitsField}>
                      <Select
                        label="Billing Data"
                        required
                        value={billingDataType}
                        onChange={setBillingDataType}
                        data={billingDataOptions}
                      />
                    </div>
                    <div className={classes.unitsField}>
                      <Select
                        label="Billing Aggregate"
                        required
                        value={billingAggregate}
                        onChange={setBillingAggregate}
                        data={[
                          { value: "daily", label: "Daily" },
                          { value: "weekly", label: "Weekly" },
                          { value: "monthly", label: "Monthly" },
                          { value: "exactdate", label: "Exact Date" },
                        ]}
                      />
                    </div>
                    <div className={classes.unitsField}>
                      <DatePickerInput
                        label="Service Start Date"
                        required
                        value={billingStartDate}
                        onChange={setBillingStartDate}
                        clearable
                        valueFormat="MM/DD/YYYY"
                        maxDate={new Date()}
                      />
                    </div>
                    <div className={classes.unitsField}>
                      <DatePickerInput
                        label="Service End Date"
                        required
                        value={billingEndDate}
                        onChange={setBillingEndDate}
                        clearable
                        valueFormat="MM/DD/YYYY"
                         minDate={billingStartDate || undefined}
                        maxDate={new Date()}
                      />
                    </div>
                  </div>
                </div>

                {/* Context bar */}
                <div className={classes.contextBar}>
                  <div className={classes.ctxCell}>
                    <span className={classes.ctxLabel}>Provider</span>
                    <span className={classes.ctxValue}>{providerLabel}</span>
                  </div>
                  <div className={classes.ctxDivider} />
                  <div className={cx(classes.ctxCell, classes.ctxCellRight)}>
                    <span className={classes.ctxLabel}>Payer</span>
                    <span className={classes.ctxValue}>{payerLabel}</span>
                  </div>
                </div>

                {/* Per-client service groups */}
                <div className={classes.svcGroupsWrap}>
                  {billingclient.length === 0 && (
                    <div className={classes.svcEmpty}>
                      No clients selected. Go back to Step 3 and pick at least one client.
                    </div>
                  )}
                  {clientRows.map(({ patient, initials, profileImg, isOpen, serviceRows, clientSubtotal }) => (
                    <div
                      key={patient.id}
                      className={classes.svcGroup}
                    >
                      <button
                        type="button"
                        className={cx(classes.svcGroupHead, {
                          [classes.svcGroupHeadClosed]: !isOpen,
                        })}
                        onClick={() =>
                          setOpenGroups((prev) => ({
                            ...prev,
                            [patient.id]: !isOpen,
                          }))
                        }
                      >
                        <span className={cx(classes.svcChev, { [classes.svcChevOpen]: isOpen })}>
                          <LuChevronRight size={12} />
                        </span>
                        <span
                          className={classes.svcAvMini}
                          style={profileImg ? { backgroundImage: `url('${profileImg}')` } : {}}
                        >
                          {!profileImg && initials}
                        </span>
                        <span className={classes.svcGroupNm}>{patient?.name || "-"}</span>
                        <span className={classes.svcCountPill}>
                          {serviceRows.length} {serviceRows.length === 1 ? "SERVICE" : "SERVICES"}
                        </span>
                        <span className={classes.svcSubtotal}>
                          ${clientSubtotal.toFixed(2)}
                        </span>
                      </button>

                      {isOpen && (
                        <div className={classes.svcGroupBody}>
                          {serviceRows.length === 0 ? (
                            <div className={classes.svcEmpty}>No services found for this client.</div>
                          ) : (
                            <table className={classes.svcTbl}>
                              <colgroup>
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "8%" }} />
                                <col style={{ width: "12%" }} />
                                <col style={{ width: "9%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "13%" }} />
                              </colgroup>
                              <thead>
                                <tr>
                                  <th className={classes.svcTblTh}>Start Date</th>
                                  <th className={classes.svcTblTh}>End Date</th>
                                  <th className={classes.svcTblTh}>Service Name</th>
                                  <th className={classes.svcTblTh}>Code</th>
                                  <th className={classes.svcTblTh}>Value Type</th>
                                  <th className={cx(classes.svcTblTh, classes.svcTblThR)}>Approved Unit</th>
                                  <th className={cx(classes.svcTblTh, classes.svcTblThR)}>Unit Charge</th>
                                  <th className={cx(classes.svcTblTh, classes.svcTblThR)}>Service Unit</th>
                                  <th className={cx(classes.svcTblTh, classes.svcTblThR)}>Service Charge</th>
                                </tr>
                              </thead>
                              <tbody>
                                {serviceRows.map(({ svc, key, startDateStr, endDateStr, unitVal, rate, charge }, si) => (
                                  <tr
                                    key={key}
                                    className={si === serviceRows.length - 1 ? classes.svcTblTrLast : ""}
                                  >
                                    <td className={classes.svcTblTd}>
                                      {startDateStr
                                        ? moment(startDateStr, "YYYY-MM-DD").format("MM/DD/YYYY")
                                        : "-"}
                                    </td>
                                    <td className={classes.svcTblTd}>
                                      {endDateStr
                                        ? moment(endDateStr, "YYYY-MM-DD").format("MM/DD/YYYY")
                                        : "-"}
                                    </td>
                                    <td className={classes.svcTblTd}>
                                      <span className={classes.svcName}>
                                        {svc.servicetitle || "-"}
                                      </span>
                                    </td>
                                    <td className={classes.svcTblTd}>
                                      {svc.servicenumber ? (
                                        <span className={classes.codeMono}>{svc.servicenumber}</span>
                                      ) : "-"}
                                    </td>
                                    <td className={classes.svcTblTd}>
                                      {svc.servicechargetype || "-"}
                                    </td>
                                    <td className={cx(classes.svcTblTd, classes.svcTblTdR)}>
                                      {svc.servicechargeAmount || "-"}
                                    </td>
                                    <td className={cx(classes.svcTblTd, classes.svcTblTdR)}>
                                      {rate > 0 ? `$${rate.toFixed(2)}` : "-"}
                                    </td>
                                    <td className={cx(classes.svcTblTd, classes.svcTblTdR)}>
                                      <input
                                        type="number"
                                        min="0"
                                        className={classes.svcUnitsInput}
                                        value={unitVal}
                                        onChange={(e) =>
                                          setServiceUnits((prev) => ({
                                            ...prev,
                                            [key]: e.target.value,
                                          }))
                                        }
                                      />
                                    </td>
                                    <td className={cx(classes.svcTblTd, classes.svcTblTdR)}>
                                      ${charge.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Total bar */}
                <div className={classes.svcTotalBar}>
                  <div>
                    <div className={classes.svcTotalLbl}>Total Claim Amount</div>
                    <div className={classes.svcTotalSub}>
                      {totalServiceCount} {totalServiceCount === 1 ? "service" : "services"} · {totalUnitCount} units
                    </div>
                  </div>
                  <div className={classes.svcTotalAmt}>${totalAmount.toFixed(2)}</div>
                </div>
              </div>
            );
          })()}

          {/* Step 4: Review */}
          {currentStep === 4 && (() => {
            const provider = selectedProviders[0];
            const payer = payerclient[0];

            const providerSub = [
              provider?.providertype,
              (() => { const bi = provider?.billingIdentifier; const n = bi ? (typeof bi === "object" ? bi?.number || "" : bi) : ""; return n ? `NPI ${n}` : null; })(),
            ].filter(Boolean).join(" · ");

            const payerSub = [
              payer?.payerState,
              payer?.payerId ? `Payer ID ${payer.payerId}` : null,
            ].filter(Boolean).join(" · ");

            const billingDataLabel = billingDataType === "manualbilling" ? "Manual Billing" : "Billing Progress Note";
            const aggregateLabel = { daily: "Daily", weekly: "Weekly", monthly: "Monthly", exactdate: "Exact Date" }[billingAggregate] || billingAggregate;

            let totalUnits = 0;
            let totalAmount = 0;
            let totalSvcCount = 0;

            const clientGroups = billingclient.map((clientEntry) => {
              const patient = clientEntry.selectedClient;
              const initials = (patient?.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
              const profileImg = patient?.imageurl ? decrypt(patient.imageurl) : null;
              const patientServices = finalservices.filter((s) => s.patientId === patient.id);
              let clientSubtotal = 0;
              let clientUnits = 0;

              const lines = patientServices
                .map((svc, si) => {
                  const key = `${patient.id}_${si}`;
                  const units = parseFloat(serviceUnits[key]) || 0;
                  const rate = parseFloat(svc.servicechargeNumber) || 0;
                  const lineTotal = units * rate;
                  const startStr = svc.serviceDate?.split(" - ")[0] || "";
                  const endStr = svc.serviceDate?.split(" - ")[1] || "";
                  return { svc, key, units, rate, lineTotal, startStr, endStr };
                })
                .filter((line) => line.units > 0);

              lines.forEach((line) => {
                clientSubtotal += line.lineTotal;
                clientUnits += line.units;
                totalUnits += line.units;
                totalAmount += line.lineTotal;
                totalSvcCount += 1;
              });

              return { patient, initials, profileImg, lines, clientSubtotal, clientUnits };
            });

            return (
              <div>
                <div className={classes.splitNote}>
                  <span><b>Review your claim.</b> Confirm everything is correct, then submit.</span>
                </div>

                {/* Summary card */}
                <div className={classes.rvSumOuter}>
                  {/* Header row */}
                  <div className={classes.rvSumHeader}>
                    <div className={classes.rvSumMeta}>
                      <span className={classes.rvSumIcon}>
                        <LuCalendarDays size={18} />
                      </span>
                      <div>
                        <div className={classes.rvSumEyebrow}>Submission date</div>
                        <div className={classes.rvSumEmphasis}>{moment().format("MM/DD/YYYY")}</div>
                      </div>
                    </div>
                    <span className={classes.rvSumPill}>Ready to submit</span>
                  </div>

                  {/* Provider + Payer cards */}
                  <div className={classes.rvSumPair}>
                    <div className={classes.rvSumCard}>
                      <div className={classes.rvSumCardHead}>
                        <span className={classes.rvSumIconMd}><LuBuilding2 size={16} /></span>
                        <span className={classes.rvSumCardLabel}>Provider</span>
                      </div>
                      <div className={classes.rvSumCardValue}>{provider?.name || "—"}</div>
                      <div className={classes.rvSumCardSub}>{providerSub || "—"}</div>
                    </div>
                    <div className={classes.rvSumCard}>
                      <div className={classes.rvSumCardHead}>
                        <span className={classes.rvSumIconMd}><LuShieldCheck size={16} /></span>
                        <span className={classes.rvSumCardLabel}>Payer</span>
                      </div>
                      <div className={classes.rvSumCardValue}>{payer?.payerName || "—"}</div>
                      <div className={classes.rvSumCardSub}>{payerSub || "—"}</div>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className={classes.rvSumStats}>
                    <div className={classes.rvSumStat}>
                      <span className={classes.rvSumIconSm}><LuFileText size={14} /></span>
                      <div className={classes.rvSumStatText}>
                        <div className={classes.rvSumStatLbl}>Billing Data</div>
                        <div className={classes.rvSumStatVal}>{billingDataLabel}</div>
                      </div>
                    </div>
                    <div className={classes.rvSumStat}>
                      <span className={classes.rvSumIconSm}><LuClock size={14} /></span>
                      <div className={classes.rvSumStatText}>
                        <div className={classes.rvSumStatLbl}>Aggregate</div>
                        <div className={classes.rvSumStatVal}>{aggregateLabel}</div>
                      </div>
                    </div>
                    <div className={classes.rvSumStat}>
                      <span className={classes.rvSumIconSm}><LuUsers size={14} /></span>
                      <div className={classes.rvSumStatText}>
                        <div className={classes.rvSumStatLbl}>Clients</div>
                        <div className={classes.rvSumStatVal}>{billingclient.length} {billingclient.length === 1 ? "client" : "clients"}</div>
                      </div>
                    </div>
                    <div className={classes.rvSumStat}>
                      <span className={classes.rvSumIconSm}><LuBarChart2 size={14} /></span>
                      <div className={classes.rvSumStatText}>
                        <div className={classes.rvSumStatLbl}>Total Units</div>
                        <div className={classes.rvSumStatVal}>{totalUnits} units</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Services per client */}
                <h6 className={classes.rvSvcGroupsHeading}>Services per client</h6>
                <div>
                  {clientGroups.length === 0 && (
                    <div className={classes.svcEmpty}>No services. Go back and add at least one client.</div>
                  )}
                  {clientGroups.map(({ patient, initials, profileImg, lines, clientSubtotal, clientUnits }) => (
                    <div key={patient.id} className={classes.rvSvcGroup}>
                      <div className={classes.rvSvcGroupHead}>
                        <span
                          className={classes.rvSvcAvMini}
                          style={profileImg ? { backgroundImage: `url('${profileImg}')` } : {}}
                        >
                          {!profileImg && initials}
                        </span>
                        <span className={classes.rvSvcGroupNm}>{patient?.name || "—"}</span>
                        <span className={classes.rvSvcCountPill}>
                          {lines.length} {lines.length === 1 ? "service" : "services"} · {clientUnits} units
                        </span>
                        <span className={classes.rvSvcSubAmt}>${clientSubtotal.toFixed(2)}</span>
                      </div>
                      <div className={classes.rvSvcList}>
                        {lines.map(({ svc, key, units, rate, lineTotal, startStr, endStr }) => (
                          <div key={key} className={classes.rvSvcLine}>
                            <div className={classes.rvSvcLineMain}>
                              {svc.servicenumber && <span className={classes.codeMono}>{svc.servicenumber}</span>}
                              <span>{svc.servicetitle || "—"}</span>
                            </div>
                            <div className={classes.rvSvcLineMeta}>
                              {startStr ? moment(startStr, "YYYY-MM-DD").format("MM/DD/YYYY") : "-"}
                              {" – "}
                              {endStr ? moment(endStr, "YYYY-MM-DD").format("MM/DD/YYYY") : "-"}
                              {" · "}
                              {units} units × ${rate.toFixed(2)}
                            </div>
                            <div className={classes.rvSvcLineAmt}>${lineTotal.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Dark total panel */}
                <div className={classes.rvTotalPanel}>
                  <div className={classes.rvTotalLeft}>
                    <div className={classes.rvTotalIcon}><LuDollarSign size={24} /></div>
                    <div>
                      <div className={classes.rvTotalEyebrow}>Total claim amount</div>
                      <div className={classes.rvTotalChips}>
                        <span className={classes.rvTotalChip}>
                          <LuUsers size={13} />
                          <b>{billingclient.length}</b> clients
                        </span>
                        <span className={classes.rvTotalChip}>
                          <LuCalendarDays size={13} />
                          <b>{totalSvcCount}</b> services
                        </span>
                        <span className={classes.rvTotalChip}>
                          <LuBarChart2 size={13} />
                          <b>{totalUnits}</b> units
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={classes.rvTotalRight}>
                    <div className={classes.rvTotalHelper}>
                      <LuCheck size={13} />
                      Ready to submit
                    </div>
                    <div className={classes.rvTotalAmount}>
                      ${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </ScrollArea>

      {/* era-modal-foot */}
      <div className={classes.modalFoot}>
        <button
          className={classes.btnGhost}
          onClick={handleBack}
          disabled={currentStep === 0}
        >
          Back
        </button>
        {currentStep < STEPS.length - 1 ? (
          <button
            className={classes.btnAccent}
            onClick={handleNext}
            disabled={!canContinue}
          >
            Continue
            <LuArrowRight size={16} />
          </button>
        ) : (
          <button
            className={classes.btnSubmit}
            onClick={handleSubmitClaim}
            disabled={billingLoading || finalservices.length === 0}
          >
            {billingLoading ? "Submitting..." : "Submit Claim"}
            {!billingLoading && <LuCheck size={16} />}
          </button>
        )}
      </div>
    </Drawer>
  );
}

export default CreateClaimDrawer;
